/**
 * Replacement Workflow Tab for NCR Edit
 *
 * Manages the replacement process workflow including status stepper,
 * initiation form, and status progression actions. Mirrors
 * CreditWorkflowTab — a claim is resolved by either a credit or a
 * replacement, and both gate NCR closure the same way.
 */

import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  CheckCircle2, PackageCheck, XCircle, Upload, Download, Clock, Loader2,
} from 'lucide-react';
import type { NonConformanceReport as NCRType } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import FileDropZone from '../../components/common/FileDropZone';
import { ProcessStepper, REPLACEMENT_STEPS, replacementStep } from './ProcessStepper';
import FileDownloadLink from '../../components/common/FileDownloadLink';

// =========================================================================
// Replacement Workflow Status Stepper
// =========================================================================

/** T8: shared step boxes, plus the rework note when the customer sent it back. */
function ReplacementStatusStepper({ status }: { status: string }) {
  const isRejected = status === 'rejected';
  return (
    <div className="space-y-2">
      <ProcessStepper steps={REPLACEMENT_STEPS} current={replacementStep(status)} returned={isRejected} />
      {isRejected && (
        <div className="flex items-center gap-2 p-2 bg-red-50 rounded border border-red-200 text-sm text-red-700">
          <XCircle className="h-4 w-4" />
          Replacement has been rejected
        </div>
      )}
    </div>
  );
}

// =========================================================================
// Supplier: submit / re-submit the despatch details (reference required)
// =========================================================================

function SubmitDispatchForm({ saving, heading, onSubmit }: {
  saving: boolean;
  heading: string;
  onSubmit: (data: FormData) => void;
}) {
  const [form, setForm] = useState({
    replacement_reference: '', dispatch_date: '', expected_delivery_date: '',
    replacement_qty: '', supplier_notes: '',
  });
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = () => {
    if (!form.replacement_reference.trim()) { setError('Enter the dispatch / delivery note reference.'); return; }
    setError(null);
    const fd = new FormData();
    fd.append('replacement_reference', form.replacement_reference.trim());
    if (form.dispatch_date) fd.append('dispatch_date', form.dispatch_date);
    if (form.expected_delivery_date) fd.append('expected_delivery_date', form.expected_delivery_date);
    if (form.replacement_qty) fd.append('replacement_qty', form.replacement_qty);
    if (form.supplier_notes) fd.append('supplier_notes', form.supplier_notes);
    if (file) fd.append('replacement_document', file);
    onSubmit(fd);
  };

  return (
    <div className="border-t pt-4 space-y-4">
      <h4 className="font-medium text-sm">{heading}</h4>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Dispatch / Delivery Note Reference <span className="text-red-500">*</span></Label>
          <Input
            value={form.replacement_reference}
            onChange={e => setForm(f => ({ ...f, replacement_reference: e.target.value }))}
            placeholder="Dispatch reference"
          />
        </div>
        <div className="space-y-2">
          <Label>Quantity Dispatched</Label>
          <Input
            type="number"
            step="1"
            value={form.replacement_qty}
            onChange={e => setForm(f => ({ ...f, replacement_qty: e.target.value }))}
            placeholder="0"
          />
        </div>
        <div className="space-y-2">
          <Label>Dispatch Date</Label>
          <Input
            type="date"
            value={form.dispatch_date}
            onChange={e => setForm(f => ({ ...f, dispatch_date: e.target.value }))}
          />
        </div>
        <div className="space-y-2">
          <Label>Expected Delivery Date</Label>
          <Input
            type="date"
            value={form.expected_delivery_date}
            onChange={e => setForm(f => ({ ...f, expected_delivery_date: e.target.value }))}
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Dispatch / Delivery Note Document</Label>
        {/* Customer review 11 Sep (drag and drop everywhere). */}
        <FileDropZone
          size="compact"
          accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx"
          hint={file ? <>Selected: <span className="font-medium text-foreground">{file.name}</span></> : 'PDF, image, Word or Excel'}
          onFiles={(files) => setFile(files[0])}
          data-testid="ncr-replacement-note-drop-zone"
        />
      </div>
      <div className="space-y-2">
        <Label>Notes</Label>
        <Textarea
          value={form.supplier_notes}
          onChange={e => setForm(f => ({ ...f, supplier_notes: e.target.value }))}
          rows={2}
          placeholder="Optional notes..."
        />
      </div>
      {error && (
        <p className="text-sm text-red-600 flex items-center gap-1"><XCircle className="h-4 w-4" />{error}</p>
      )}
      <Button onClick={handleSubmit} disabled={saving}>
        {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
        Submit Dispatch Details
      </Button>
    </div>
  );
}

// =========================================================================
// Replacement Workflow Tab
// =========================================================================

export interface ReplacementWorkflowTabProps {
  ncr: NCRType;
  isCustomer: boolean;
  isSupplier: boolean;
  saving: boolean;
  onInitiate: (data: { replacement_qty?: number; replacement_reason?: string; customer_notes?: string }) => void;
  onSubmitDispatch: (data: FormData) => void;
  onUpdateStatus: (status: string, data?: Record<string, unknown>) => void;
}

export function ReplacementWorkflowTab({ ncr, isCustomer, isSupplier, saving, onInitiate, onSubmitDispatch, onUpdateStatus }: ReplacementWorkflowTabProps) {
  const [initForm, setInitForm] = useState({ replacement_qty: '', replacement_reason: '', customer_notes: '' });
  const { formatDate, formatQuantity } = useFormatters();
  const [approvalForm, setApprovalForm] = useState({ customer_replacement_reference: '', approval_notes: '' });

  const process = ncr.replacement_process;

  if (!ncr.is_replacement_required) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-8 text-muted-foreground">
            Replacement is not required for this NCR.
          </div>
        </CardContent>
      </Card>
    );
  }

  // If no process exists yet, show initiation form
  if (!process) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PackageCheck className="h-5 w-5" />
            Initiate Replacement Process
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Replacement parts are required for this NCR. Start the replacement process below.
          </p>
          {isCustomer ? (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Replacement Quantity</Label>
                  <Input
                    type="number"
                    step="1"
                    value={initForm.replacement_qty}
                    onChange={e => setInitForm(f => ({ ...f, replacement_qty: e.target.value }))}
                    placeholder={String(ncr.reject_qty ?? 0)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Replacement Reason</Label>
                  <Input
                    value={initForm.replacement_reason}
                    onChange={e => setInitForm(f => ({ ...f, replacement_reason: e.target.value }))}
                    placeholder="Reason / instructions for the replacement"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={initForm.customer_notes}
                  onChange={e => setInitForm(f => ({ ...f, customer_notes: e.target.value }))}
                  placeholder="Additional details..."
                  rows={3}
                />
              </div>
              <Button
                onClick={() => onInitiate({
                  replacement_qty: initForm.replacement_qty ? parseFloat(initForm.replacement_qty) : undefined,
                  replacement_reason: initForm.replacement_reason,
                  customer_notes: initForm.customer_notes,
                })}
                disabled={saving}
              >
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PackageCheck className="h-4 w-4 mr-2" />}
                Initiate Replacement
              </Button>
            </>
          ) : (
            <p className="text-sm text-amber-600">Waiting for the customer to initiate the replacement process.</p>
          )}
        </CardContent>
      </Card>
    );
  }

  // Process exists - show status stepper and actions
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <PackageCheck className="h-5 w-5" />
          Replacement Process
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <ReplacementStatusStepper status={process.status} />

        {/* Process details */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Requested by:</span>{' '}
            <span className="font-medium">{process.requested_by_name}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Requested:</span>{' '}
            <span className="font-medium">{formatDate(process.requested_date)}</span>
          </div>
          {process.replacement_qty && (
            <div>
              <span className="text-muted-foreground">Quantity:</span>{' '}
              <span className="font-medium">{formatQuantity(process.replacement_qty)}</span>
            </div>
          )}
          {process.replacement_reason && (
            <div className="col-span-2">
              <span className="text-muted-foreground">Reason:</span>{' '}
              <span className="font-medium">{process.replacement_reason}</span>
            </div>
          )}
          {process.replacement_reference && (
            <div>
              <span className="text-muted-foreground">Dispatch ref:</span>{' '}
              <span className="font-medium">{process.replacement_reference}</span>
            </div>
          )}
          {process.dispatch_date && (
            <div>
              <span className="text-muted-foreground">Dispatched:</span>{' '}
              <span className="font-medium">{formatDate(process.dispatch_date)}</span>
            </div>
          )}
          {process.expected_delivery_date && (
            <div>
              <span className="text-muted-foreground">Expected delivery:</span>{' '}
              <span className="font-medium">{formatDate(process.expected_delivery_date)}</span>
            </div>
          )}
          {/* CR-7482: fetched through the API client — see the Credit tab. */}
          {process.replacement_document_url && (
            <div>
              <span className="text-muted-foreground">Dispatch note:</span>{' '}
              <FileDownloadLink
                url={process.replacement_document_url}
                fallbackName="despatch-note"
                icon={<Download className="h-3.5 w-3.5" />}
                className="font-medium text-primary inline-flex items-center gap-1 hover:underline disabled:opacity-60 disabled:cursor-not-allowed"
              >
                View document
              </FileDownloadLink>
            </div>
          )}
          {process.customer_replacement_reference && (
            <div>
              <span className="text-muted-foreground">Customer ref:</span>{' '}
              <span className="font-medium">{process.customer_replacement_reference}</span>
            </div>
          )}
        </div>

        {/* Notes display */}
        {(process.customer_notes || process.supplier_notes || process.approval_notes) && (
          <div className="space-y-2 border-t pt-4">
            {process.customer_notes && (
              <div className="text-sm"><span className="text-muted-foreground">Customer notes:</span> {process.customer_notes}</div>
            )}
            {process.supplier_notes && (
              <div className="text-sm"><span className="text-muted-foreground">Supplier notes:</span> {process.supplier_notes}</div>
            )}
            {process.approval_notes && (
              <div className="text-sm"><span className="text-muted-foreground">Approval notes:</span> {process.approval_notes}</div>
            )}
          </div>
        )}

        {/* Actions based on current status */}

        {/* Supplier: submit despatch details (requested -> dispatched) */}
        {process.status === 'requested' && isSupplier && (
          <SubmitDispatchForm saving={saving} heading="Submit Dispatch Details" onSubmit={onSubmitDispatch} />
        )}

        {/* Customer: waiting for the supplier's despatch */}
        {process.status === 'requested' && isCustomer && (
          <div className="border-t pt-4 flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" /> Awaiting the supplier to dispatch the replacement.
          </div>
        )}

        {/* Customer: confirm receipt or reject the despatched replacement */}
        {process.status === 'dispatched' && isCustomer && (
          <div className="border-t pt-4 space-y-4">
            <h4 className="font-medium text-sm">Confirm Replacement Receipt</h4>
            <div className="space-y-2">
              <Label>Customer Reference</Label>
              <Input
                value={approvalForm.customer_replacement_reference}
                onChange={e => setApprovalForm(f => ({ ...f, customer_replacement_reference: e.target.value }))}
                placeholder="Your internal reference (e.g. GRN)"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={approvalForm.approval_notes}
                onChange={e => setApprovalForm(f => ({ ...f, approval_notes: e.target.value }))}
                placeholder="Confirmation or rejection notes..."
                rows={2}
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={() => onUpdateStatus('approved', {
                customer_replacement_reference: approvalForm.customer_replacement_reference,
                approval_notes: approvalForm.approval_notes,
              })} disabled={saving} className="bg-green-600 hover:bg-green-700">
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Confirm Receipt &amp; Close
              </Button>
              <Button variant="destructive" onClick={() => onUpdateStatus('rejected', {
                approval_notes: approvalForm.approval_notes,
              })} disabled={saving}>
                <XCircle className="h-4 w-4 mr-2" />
                Reject
              </Button>
            </div>
          </div>
        )}

        {/* Supplier: waiting for customer confirmation */}
        {process.status === 'dispatched' && isSupplier && (
          <div className="border-t pt-4 flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" /> Dispatch details submitted. Awaiting customer receipt confirmation.
          </div>
        )}

        {/* Supplier: amend and re-submit after a rejection */}
        {process.status === 'rejected' && isSupplier && (
          <SubmitDispatchForm saving={saving} heading="Amend &amp; Re-submit Dispatch Details" onSubmit={onSubmitDispatch} />
        )}

        {/* Customer: waiting for the supplier to resubmit */}
        {process.status === 'rejected' && isCustomer && (
          <div className="border-t pt-4 flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" /> Rejected. Awaiting the supplier to amend and resubmit.
          </div>
        )}

        {process.status === 'completed' && (
          <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            <div>
              <h3 className="font-semibold text-green-800">Replacement Completed</h3>
              {process.completed_date && (
                <p className="text-sm text-green-600">
                  Completed on {formatDate(process.completed_date)}
                  {process.completed_by_name && ` by ${process.completed_by_name}`}
                </p>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
